import type { DealerReport, DealerReportAnswer, ReportDocument } from "@hakili/contracts";
import { formatDate, t } from "@hakili/i18n";
import type { ReactElement } from "react";

/**
 * Lane E, the report round, M2 (record of 9 September): **the dealer opens the report standing on
 * his car , the cabinet's paper and Hakili's sheet (« Lecture Hakili ») , and nothing of the
 * buyer.** `GET /api/v1/me/vehicles/{id}/rapport` answers the two documents with their signed
 * links; the card draws one link per document, each named for what it opens, and says in words
 * which one is missing instead of drawing a dead link. A car with no report draws nothing.
 */
/** One of the report's two documents, as the dealer's door sends it (the contract). */
export type ReportDocumentLink = ReportDocument;

/** The standing report on a car, as the dealer's door sends it (the contract): nothing of the buyer. */
export type VehicleReportView = DealerReport;

/** The report the answer carries, or `null` while none stands on the car. */
export function reportFrom(answer: DealerReportAnswer): VehicleReportView | null {
  return answer.report;
}

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

export function ReportCard(props: { readonly view: VehicleReportView | null }): ReactElement | null {
  const { view } = props;
  if (view === null) return null;
  const linkClass = "rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900";
  return (
    <section className="mt-4 rounded-2xl border border-gray-200 bg-white p-4" data-testid="vehicule-rapport">
      <h3 className="text-base font-semibold text-gray-900">{t("dealer.rapport.titre")}</h3>
      <p className="mt-1 text-sm text-gray-600">
        {fill("dealer.rapport.cabinet", { cabinet: view.cabinetName, version: String(view.version) })}
      </p>
      <p className="mt-1 text-sm text-gray-600" data-testid="vehicule-rapport-validite">
        {fill("dealer.rapport.valable", { date: formatDate(new Date(`${view.validUntil}T00:00:00Z`)) })}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {view.paper === null ? (
          <p className="text-sm text-gray-500" data-testid="vehicule-rapport-papier-absent">
            {t("dealer.rapport.papierAbsent")}
          </p>
        ) : (
          <a
            href={view.paper.url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            data-testid="vehicule-rapport-papier"
          >
            {t("dealer.rapport.papierOuvrir")}
          </a>
        )}
        {view.sheet === null ? (
          <p className="text-sm text-gray-500" data-testid="vehicule-rapport-lecture-absente">
            {t("dealer.rapport.lectureAbsente")}
          </p>
        ) : (
          <a
            href={view.sheet.url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            data-testid="vehicule-rapport-lecture"
          >
            {t("dealer.rapport.lectureOuvrir")}
          </a>
        )}
      </div>
    </section>
  );
}
